import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SubtitleCard from "@/components/base/SubtitleCard";
import SectionHeading from "@/components/base/SectionHeading";
import SubtitlePreview from "@/pages/detail/components/SubtitlePreview";
import DownloadSection from "@/pages/detail/components/DownloadSection";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { allCategories, getSubtitleById, getRelatedSubtitles } from "@/utils/subtitles";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const item = id ? getSubtitleById(id) : undefined;

  useDocumentTitle(
    item
      ? `${item.title} ${item.format.toUpperCase()} - 免费中文字幕下载`
      : "字幕资源 - 中文在线字幕免费高清",
    item
      ? `${item.title}（${item.year}，${item.type}）${item.language}高清中文字幕，${item.format.toUpperCase()} 格式，文件大小 ${item.size}，更新时间 ${item.updatedAt}。`
      : "字幕资源详情页。",
  );

  if (!item) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-24 text-center md:px-6">
        <i className="ri-link-unlink-m text-5xl text-accent-500"></i>
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">
          资源暂不可用，请稍后再试
        </h1>
        <p className="mt-2 text-sm text-foreground-500">
          该字幕资源可能已下架或链接失效，返回分类页看看其他资源吧。
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link
            to="/"
            className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            返回首页
          </Link>
          <Link
            to="/search"
            className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300"
          >
            搜索其他字幕
          </Link>
        </div>
      </div>
    );
  }

  const category = allCategories.find((entry) => entry.slug === item.typeSlug);
  const related = getRelatedSubtitles(item, 4);

  const metaItems = [
    { icon: "ri-price-tag-3-line", label: "资源类型", value: item.type },
    { icon: "ri-calendar-line", label: "影片年份", value: `${item.year}` },
    { icon: "ri-file-text-line", label: "字幕格式", value: item.format.toUpperCase() },
    { icon: "ri-translate-2", label: "字幕语言", value: item.language },
    { icon: "ri-hard-drive-2-line", label: "文件大小", value: item.size },
    { icon: "ri-time-line", label: "更新时间", value: item.updatedAt },
    { icon: "ri-timer-line", label: "片长 / 集数", value: item.runtime },
    { icon: "ri-map-pin-line", label: "出品地区", value: item.region },
  ];

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6">
      <Breadcrumb
        items={[
          { label: category ? category.name : item.type, to: `/category/${item.typeSlug}` },
          { label: item.title },
        ]}
      />

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[300px_1fr]">
        <div className="lg:mx-0">
          <div className="mx-auto w-full max-w-[300px] overflow-hidden rounded-lg border border-background-200 bg-background-100 lg:mx-0">
            <div className="aspect-[2/3] w-full">
              <img
                src={item.cover}
                alt={`${item.title}（${item.year}）${item.language}中文字幕封面图`}
                title={`${item.title} 中文字幕下载 - ${item.format.toUpperCase()}`}
                className="h-full w-full object-top"
              />
            </div>
          </div>
          <div className="mx-auto mt-4 flex max-w-[300px] items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3 lg:mx-0">
            <span className="flex items-center gap-1.5 text-sm font-semibold text-accent-700">
              <i className="ri-star-fill"></i>
              {item.rating.toFixed(1)}
            </span>
            <span className="text-xs text-foreground-500">
              综合评分 · {item.downloads.toLocaleString()} 次下载
            </span>
          </div>
        </div>

        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
            {item.title}
            <span className="ml-3 align-middle text-base font-medium text-foreground-500">
              {item.language}字幕
            </span>
          </h1>
          <p className="mt-2 text-sm text-foreground-500">{item.originalTitle}</p>

          <div className="mt-3 flex flex-wrap gap-2">
            {item.tags.map((tag) => (
              <span
                key={tag}
                className="whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900"
              >
                {tag}
              </span>
            ))}
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {metaItems.map((meta) => (
              <div
                key={meta.label}
                className="rounded-md border border-background-200 bg-background-50 p-3"
              >
                <p className="flex items-center gap-1 text-xs text-foreground-500">
                  <i className={`${meta.icon} text-sm text-primary-500`}></i>
                  {meta.label}
                </p>
                <p className="mt-1 truncate text-sm font-semibold text-foreground-900" title={meta.value}>
                  {meta.value}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-5 rounded-lg border border-background-200 bg-background-100 p-4">
            <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
              <i className="ri-file-info-line text-lg text-primary-500"></i>
              字幕简介
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-700">{item.summary}</p>
          </div>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
        <SubtitlePreview versions={item.versions} title={item.title} />
        <DownloadSection versions={item.versions} title={item.title} />
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3">
        <p className="text-sm text-foreground-600">
          想看看同类型的其他字幕？
        </p>
        <div className="flex flex-wrap gap-3">
          <Link
            to={`/category/${item.typeSlug}`}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line"></i>
            返回{category ? category.name : "分类"}
          </Link>
          <Link
            to="/search"
            className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300"
          >
            <i className="ri-search-line"></i>
            搜索更多
          </Link>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-12">
          <SectionHeading
            title="相关推荐字幕"
            subtitle="根据类型与标签为你匹配的其他字幕资源"
            icon="ri-magic-line"
          />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {related.map((entry) => (
              <SubtitleCard key={entry.id} item={entry} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}